import React from 'react';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Alert, Box, Typography } from '@mui/material';

import { getAttachmentScanState } from '@/components/NewUI/Components/Utilities/attachmentScan';

import { QuestionTitleHtml } from './QuestionTitleHtml';
import { SectionHeading } from './SectionHeading';
import { classes } from './styles';

import type { PreviewSnapshot } from './types';

interface PreviewProps {
  personalInformationLabel?: string;
  previewAriaLabel?: string;
  previewInfoBannerText?: string;
  nothingEnteredText?: string;
  nothingSubmittedText?: string;
  onAttachmentClick?: (attachment: PreviewSnapshot['attachments'][number]) => void;
  snapshot: PreviewSnapshot;
  fontFamily?: string;
}

const DEFAULT_NOTHING_ENTERED_TEXT = 'Nothing entered';
const DEFAULT_NOTHING_SUBMITTED_TEXT = 'Nothing submitted';
const DEFAULT_PREVIEW_ARIA_LABEL = 'Application preview';

const isEmptyValue = (
  value: string,
  nothingEnteredText: string,
  nothingSubmittedText: string
): boolean =>
  value.trim().length === 0 || value === nothingEnteredText || value === nothingSubmittedText;

const canOpenAttachment = (
  attachment: PreviewSnapshot['attachments'][number],
  nothingEnteredText: string,
  nothingSubmittedText: string,
  onAttachmentClick?: PreviewProps['onAttachmentClick']
): boolean =>
  Boolean(
    onAttachmentClick &&
    getAttachmentScanState({
      malware_scan_status: attachment.malwareScanStatus,
      available_for_serving: attachment.availableForServing
    }) === 'available' &&
    attachment.id !== null &&
    attachment.id !== undefined &&
    !isEmptyValue(attachment.fileName, nothingEnteredText, nothingSubmittedText)
  );

export const Preview: React.FC<PreviewProps> = ({
  snapshot,
  personalInformationLabel = 'Personal details',
  previewAriaLabel = DEFAULT_PREVIEW_ARIA_LABEL,
  previewInfoBannerText = 'Your application has not been submitted yet. Please review your answers and click the Submit button at the bottom of the page to finalise your application.',
  nothingEnteredText = DEFAULT_NOTHING_ENTERED_TEXT,
  nothingSubmittedText = DEFAULT_NOTHING_SUBMITTED_TEXT,
  onAttachmentClick,
  fontFamily
}) => {
  const sourceName = snapshot.source?.name?.trim();
  const sourceValue = sourceName && sourceName.length > 0 ? sourceName : nothingEnteredText;

  return (
    <Box aria-label={previewAriaLabel} component="section" sx={classes.preview.containerSx()}>
      <Alert
        aria-live="polite"
        icon={<InfoOutlinedIcon />}
        role="status"
        severity="info"
        sx={classes.preview.infoBannerSx()}
      >
        <Typography sx={classes.preview.infoBannerTextSx()}>{previewInfoBannerText}</Typography>
      </Alert>

      {snapshot.privacy ? (
        <Box
          aria-labelledby="preview-privacy-heading"
          component="section"
          sx={classes.preview.sectionSx()}
        >
          <SectionHeading fontFamily={fontFamily} id="preview-privacy-heading">
            {snapshot.privacy.headingText}
          </SectionHeading>
          <Box
            className="wcag-rich-text"
            dangerouslySetInnerHTML={{ __html: snapshot.privacy.descriptionHtml }}
            sx={classes.preview.privacyDescriptionSx()}
          />
          <Typography sx={classes.preview.privacyAgreeLineSx()}>
            {snapshot.privacy.agreeLabel}
          </Typography>
        </Box>
      ) : null}

      <Box
        aria-labelledby="preview-personal-details-heading"
        component="section"
        sx={classes.preview.sectionSx()}
      >
        <SectionHeading fontFamily={fontFamily} id="preview-personal-details-heading">
          {personalInformationLabel}
        </SectionHeading>
        {snapshot.candidateFields.length > 0 ? (
          <Box sx={classes.preview.fieldGridSx()}>
            {snapshot.candidateFields.map((field) => (
              <Box key={field.label} sx={classes.preview.fieldItemSx(field.fullWidth)}>
                <Typography sx={classes.preview.fieldLabelSx()}>
                  {field.label}
                  {field.required ? (
                    <Box component="span" sx={classes.preview.requiredAsteriskSx()}>
                      *
                    </Box>
                  ) : null}
                </Typography>
                <Typography
                  sx={classes.preview.fieldValueSx(
                    isEmptyValue(field.value, nothingEnteredText, nothingSubmittedText)
                  )}
                >
                  {field.value}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography sx={classes.preview.fieldValueSx(true)}>{nothingEnteredText}</Typography>
        )}
      </Box>

      {snapshot.hasQuestionsSection ? (
        <Box
          aria-labelledby="preview-questions-heading"
          component="section"
          sx={classes.preview.sectionSx()}
        >
          <SectionHeading fontFamily={fontFamily} id="preview-questions-heading">
            Questions
          </SectionHeading>
          {snapshot.questions.length > 0 ? (
            snapshot.questions.map((q, index) => (
              <Box key={q.fieldId} sx={classes.preview.questionFieldSx()}>
                <Typography sx={classes.preview.fieldLabelSx()}>
                  {index + 1}. <QuestionTitleHtml html={q.title} />
                  {q.required ? (
                    <Box component="span" sx={classes.preview.requiredAsteriskSx()}>
                      *
                    </Box>
                  ) : null}
                </Typography>
                <Typography
                  sx={classes.preview.fieldValueSx(
                    isEmptyValue(q.answer, nothingEnteredText, nothingSubmittedText)
                  )}
                >
                  {q.answer}
                </Typography>
              </Box>
            ))
          ) : (
            <Typography sx={classes.preview.fieldValueSx(true)}>{nothingEnteredText}</Typography>
          )}
        </Box>
      ) : null}

      {snapshot.hasSourceSection ? (
        <Box
          aria-labelledby="preview-source-selection-heading"
          component="section"
          sx={classes.preview.sectionSx()}
        >
          <SectionHeading fontFamily={fontFamily} id="preview-source-selection-heading">
            Source Selection
          </SectionHeading>
          <Box sx={classes.preview.fieldItemSx()}>
            <Typography sx={classes.preview.fieldLabelSx()}>
              Where did you hear about this job?
              <Box component="span" sx={classes.preview.requiredAsteriskSx()}>
                *
              </Box>
            </Typography>
            <Typography
              sx={classes.preview.fieldValueSx(
                isEmptyValue(sourceValue, nothingEnteredText, nothingSubmittedText)
              )}
            >
              {sourceValue}
            </Typography>
          </Box>
        </Box>
      ) : null}

      {snapshot.hasAttachmentsSection ? (
        <Box
          aria-labelledby="preview-attachments-heading"
          component="section"
          sx={classes.preview.sectionSx()}
        >
          {/* Preview Attachments uses default 32px variant (not the edit-mode
              helperText 8px variant) — preview has no global hint below the
              heading, so the standard 32px gap to the first item is correct. */}
          <SectionHeading fontFamily={fontFamily} id="preview-attachments-heading">
            Attachments
          </SectionHeading>
          {snapshot.attachments.map((att) => {
            const isEmpty = isEmptyValue(att.fileName, nothingEnteredText, nothingSubmittedText);
            const scanState =
              isEmpty || att.id === null || att.id === undefined
                ? 'available'
                : getAttachmentScanState({
                    malware_scan_status: att.malwareScanStatus,
                    available_for_serving: att.availableForServing
                  });
            const clickable = canOpenAttachment(
              att,
              nothingEnteredText,
              nothingSubmittedText,
              onAttachmentClick
            );
            const attachmentValue =
              (scanState === 'scanning' && 'Scanning') ||
              (scanState === 'unavailable' && 'File unavailable') ||
              att.fileName;

            return (
              <Box key={att.name} sx={classes.preview.questionFieldSx()}>
                <Typography sx={classes.preview.fieldLabelSx()}>
                  {att.name}
                  {att.mandatory ? (
                    <Box component="span" sx={classes.preview.requiredAsteriskSx()}>
                      *
                    </Box>
                  ) : null}
                </Typography>
                {clickable ? (
                  <Box
                    component="button"
                    onClick={() => onAttachmentClick?.(att)}
                    type="button"
                    sx={[
                      classes.preview.fieldValueSx(isEmpty),
                      {
                        background: 'none',
                        border: 0,
                        cursor: 'pointer',
                        margin: 0,
                        padding: 0,
                        textAlign: 'left',
                        textDecoration: 'underline',
                        '&:focus-visible': {
                          outline: '2px solid currentColor',
                          outlineOffset: '2px'
                        }
                      }
                    ]}
                  >
                    {attachmentValue}
                  </Box>
                ) : (
                  <Typography
                    sx={classes.preview.fieldValueSx(isEmpty)}
                    data-testid={
                      scanState === 'available' ? undefined : 'candidate-attachment-scan-status'
                    }
                  >
                    {attachmentValue}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      ) : null}
    </Box>
  );
};

export default Preview;
